import React, { useState } from 'react';
import { LuMinus, LuPlus, LuLock } from 'react-icons/lu';
import SugerirDia from './SugerirDia';

// Cabecera de un día en la Agenda de Trabajos (27-oct-2026): lo que antes era la "Semana" (calendario de
// zonas) va arriba de las franjas del día. Zona, cupo, lo que reservó la empresa (provisorio hasta armar el
// día, con "Sugerir otro día") y los dos botones: ✨ Armar este día y Cambiar zona (zona, cupo y lugar para
// urgencias de ese día; la semana tipo sigue en Rentabilidad → Ajustes).
export const ZONAS = [['CABA', 'CABA'], ['SUR', 'Sur'], ['OTRA', 'Otras'], ['LIBRE', 'Libre'], ['NO', 'No se trabaja']];
const ZONA_CLASE = { CABA: 'bg-[#1E3A8A] text-[#DBEAFE]', SUR: 'bg-[#134E4A] text-[#CCFBF1]', OTRA: 'bg-[#5B21B6] text-[#EDE9FE]', NO: 'bg-chip text-muted' };
export const zonaCorta = (z) => ({ CABA: 'CABA', SUR: 'Sur', OTRA: 'Otras', LIBRE: 'Libre', NO: 'No' }[z] || z || '');

function Paso({ label, valor, min, max, onCambio, disabled }) {
    const b = 'w-9 h-9 rounded-lg bg-chip text-ink inline-flex items-center justify-center active:scale-95 disabled:opacity-40';
    return (
        <span className="inline-flex items-center gap-1.5 text-caption text-secondary">{label}
            <button type="button" aria-label={`Menos ${label.toLowerCase()}`} className={b} disabled={disabled || valor <= min} onClick={() => onCambio(valor - 1)}><LuMinus size={14} /></button>
            <b className="w-5 text-center text-body text-ink">{valor}</b>
            <button type="button" aria-label={`Más ${label.toLowerCase()}`} className={b} disabled={disabled || valor >= max} onClick={() => onCambio(valor + 1)}><LuPlus size={14} /></button>
        </span>
    );
}

export default function DiaZona({ d, dias, guardando, onCambiar, onArmar, onListo, extra = null }) {
    const [editar, setEditar] = useState(false);
    const pct = Math.min(100, Math.round((d.usado / Math.max(d.cupo, 1)) * 100));
    const agendados = (d.agendados || []).filter(o => !o.hecho).length;
    const res = d.reservados || [];
    const resumen = d.trabaja
        ? [`${d.usado} de ${d.cupo} equipos`, agendados ? `${agendados} agendado${agendados !== 1 ? 's' : ''}` : null,
            res.length ? `${res.length} reservado${res.length !== 1 ? 's' : ''} por la empresa` : null].filter(Boolean).join(' · ')
        : 'No se trabaja';
    return (
        <div className="space-y-2">
            {extra}
            <div className={`p-3.5 rounded-2xl bg-card border border-black/[0.06] dark:border-white/[0.06] space-y-2.5 ${d.trabaja ? '' : 'opacity-80'}`}>
                <div className="flex items-center gap-2">
                    <span className="flex-1 min-w-0 text-body-lg font-black text-ink capitalize">{d.dia}</span>
                    {d.cerrado && <span className="h-6 px-2 rounded-md bg-chip text-secondary text-label font-black uppercase inline-flex items-center gap-1"><LuLock size={11} /> Cerrado</span>}
                    <span className={`h-6 px-2 rounded-md text-label font-black uppercase inline-flex items-center ${ZONA_CLASE[d.zona] || 'bg-chip text-ink'}`}>
                        {d.zona === 'NO' ? 'No se trabaja' : `Zona ${zonaCorta(d.zona)}`}
                    </span>
                </div>
                {d.trabaja && (
                    <span className="block h-2 rounded-full bg-chip overflow-hidden">
                        <span className={`block h-full rounded-full ${pct >= 100 ? 'bg-brand-red' : 'bg-[#2BB3A3]'}`} style={{ width: `${pct}%` }} />
                    </span>
                )}
                <p className="text-caption text-muted">{resumen}{d.urgentes > 0 && <span className="text-brand-red font-bold"> · {d.urgentes} urgente{d.urgentes !== 1 ? 's' : ''}</span>}</p>

                {res.length > 0 && (
                    <div className="rounded-xl border border-dashed border-black/15 dark:border-white/20 px-3 py-1">
                        {res.map(p => (
                            <div key={p.pedidoId} className="flex items-start gap-2 py-2 border-t first:border-t-0 border-black/[0.06] dark:border-white/[0.06]">
                                <span className="flex-1 min-w-0">
                                    <span className="block text-body font-black text-ink truncate">{p.nombre}</span>
                                    <span className="block text-caption text-muted truncate">{[p.motivo, p.equipos > 1 ? `${p.equipos} equipos` : null, p.direccion].filter(Boolean).join(' · ')}</span>
                                    {!d.cerrado && <SugerirDia p={p} dias={dias} onListo={onListo} />}
                                </span>
                                <span className={`h-6 px-2 shrink-0 rounded-md text-label font-black uppercase inline-flex items-center ${p.urgente ? 'bg-brand-red text-white' : 'bg-chip text-secondary'}`}>
                                    {p.urgente ? 'Urgente' : 'Reservado'}
                                </span>
                            </div>
                        ))}
                    </div>
                )}

                {/* Cerrado = la empresa ya no puede reservar ese día; el admin lo arma igual */}
                {(d.trabaja || !d.cerrado) && (
                    <div className="flex gap-2">
                        {d.trabaja && (
                            <button type="button" onClick={onArmar} disabled={guardando}
                                className="flex-[2] h-11 rounded-xl bg-brand-red text-white text-label font-black inline-flex items-center justify-center gap-1.5 active:scale-95 disabled:opacity-40">
                                <span aria-hidden>✨</span> Armar este día
                            </button>
                        )}
                        {!d.cerrado && (
                            <button type="button" onClick={() => setEditar(e => !e)} aria-expanded={editar}
                                className="flex-1 h-11 rounded-xl bg-chip text-ink text-label font-black active:scale-95">{editar ? 'Listo' : 'Cambiar zona'}</button>
                        )}
                    </div>
                )}
                {editar && !d.cerrado && (
                    <div className="space-y-2.5 pt-1">
                        <div className="flex gap-1.5 flex-wrap">
                            {ZONAS.map(([id, label]) => (
                                <button key={id} type="button" disabled={guardando} onClick={() => d.zona !== id && onCambiar({ zona: id })} aria-pressed={d.zona === id}
                                    className={`h-9 px-3 rounded-lg text-label font-black disabled:opacity-60 ${d.zona === id ? (ZONA_CLASE[id] || 'bg-ink text-page') : 'bg-panel text-secondary'}`}>{label}</button>
                            ))}
                        </div>
                        {d.trabaja && (
                            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
                                <Paso label="Cupo" valor={d.cupo} min={1} max={20} disabled={guardando} onCambio={v => onCambiar({ cupo: v })} />
                                <Paso label="Para urgencias" valor={d.reserva} min={0} max={5} disabled={guardando} onCambio={v => onCambiar({ reserva: v })} />
                            </div>
                        )}
                        <p className="text-caption text-muted">Cambia solo este día. La semana tipo está en Rentabilidad → Ajustes.</p>
                    </div>
                )}
            </div>
        </div>
    );
}
